import type { ButtonHTMLAttributes, ReactNode } from "react";

type Variant = "primary" | "secondary" | "ghost" | "info";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  children: ReactNode;
}

/**
 * One ember-solid action per screen (brand rule).
 *
 * `info` exists for exactly one sanctioned exception: the upgrade wall's
 * ask-a-parent CTA, which the spec requires to carry equal weight to Pay
 * because for an under-18 the share path *is* the conversion path.
 *
 * Disabled dims to 40% rather than greying out — the comps keep the button
 * legible so a student can read what they are working towards.
 */
const variants: Record<Variant, string> = {
  primary: "bg-accent text-on-accent active:bg-accent-pressed",
  info: "bg-info text-white active:brightness-90",
  secondary: "bg-surface-high text-text border border-border-strong active:bg-surface",
  ghost: "bg-transparent text-text-muted active:text-text",
};

export function Button({ variant = "primary", className = "", ...props }: ButtonProps) {
  return (
    <button
      // min-h-12 is the 48px touch target the brand rules require.
      className={`w-full min-h-12 rounded-[var(--radius-control)] px-4 text-[15px] font-semibold
        transition-[opacity,background-color] disabled:opacity-40 disabled:cursor-not-allowed
        ${variants[variant]} ${className}`}
      {...props}
    />
  );
}
